import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import api from '../lib/api';

import OrderCard from './approvals/OrderCard';
import SEStockCard from './approvals/SEStockCard';
import RejectModal from './approvals/RejectModal';
import DesignImageModal from './approvals/DesignImageModal';

export default function Approvals({ user }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ pending_sos: [], pending_pos: [], pending_se_stock: [], rejected_sos: [], rejected_pos: [], rejected_se_stock: [] });
  const [activeTab, setActiveTab] = useState('so');
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(null);
  const [expandedOrders, setExpandedOrders] = useState({});
  const [designImage, setDesignImage] = useState(null);

  const toggleExpanded = (id) => {
    setExpandedOrders(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const openDesignImage = async (dc, e) => {
    if (e) e.stopPropagation();
    setDesignImage({ design_code: dc, images: [], loading: true });
    try {
      const res = await api.get(`/products/design-image/${dc}`);
      setDesignImage({ design_code: dc, images: res.data.images || [], loading: false });
    } catch {
      setDesignImage({ design_code: dc, images: [], loading: false });
    }
  };

  const loadData = useCallback(async () => {
    try {
      const res = await api.get('/approvals/pending');
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load approvals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleApprove = async (type, id, number) => {
    setProcessing(id);
    try {
      await api.post(`/approvals/${type}/${id}/approve`);
      toast.success(`${number} approved successfully`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(null);
    }
  };

  const handleApproveSEStock = async (req) => {
    setProcessing(req.id);
    try {
      await api.post(`/approvals/se-stock/${req.id}/approve`);
      toast.success('SE approved and created');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    setProcessing(rejectModal.id);
    try {
      await api.post(`/approvals/${rejectModal.type}/${rejectModal.id}/reject`, { reason: rejectReason });
      toast.success(`${rejectModal.number} rejected`);
      setRejectModal(null);
      setRejectReason('');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(null);
    }
  };

  const tabs = [
    { key: 'so', label: 'Sales Orders', pendingCount: data.pending_sos.length, rejectedCount: data.rejected_sos.length },
    { key: 'po', label: 'Purchase Orders', pendingCount: data.pending_pos.length, rejectedCount: data.rejected_pos.length },
    { key: 'se_stock', label: 'SE Stock Approvals', pendingCount: data.pending_se_stock.length, rejectedCount: data.rejected_se_stock.length },
  ];

  const pendingItems = activeTab === 'so' ? data.pending_sos : activeTab === 'po' ? data.pending_pos : [];
  const rejectedItems = activeTab === 'so' ? data.rejected_sos : activeTab === 'po' ? data.rejected_pos : [];
  const type = activeTab === 'so' ? 'so' : activeTab === 'po' ? 'po' : 'se-stock';

  const renderOrderCard = (order, isPending) => (
    <OrderCard
      key={order.id}
      order={order} isPending={isPending} activeTab={activeTab}
      isExpanded={!!expandedOrders[order.id]} onToggleExpand={() => toggleExpanded(order.id)}
      onOpenDesignImage={openDesignImage} processing={processing}
      onApprove={(number) => handleApprove(type, order.id, number)}
      onReject={(number) => { setRejectModal({ type, id: order.id, number }); setRejectReason(''); }}
    />
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="approvals-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }} data-testid="approvals-title">
          Approvals
        </h1>
        <p className="text-sm text-gray-500 mt-1">Review and approve pending Sales Orders and Purchase Orders</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-0" data-testid="approval-tabs">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === tab.key
                ? 'border-amber-500 text-amber-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            data-testid={`tab-${tab.key}`}
          >
            {tab.label}
            {tab.pendingCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold bg-amber-500 text-white">
                {tab.pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Pending Section */}
      {activeTab !== 'se_stock' ? (
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-amber-500"></span>
          Pending Approval
          {pendingItems.length > 0 && <span className="text-sm font-normal text-gray-500">({pendingItems.length})</span>}
        </h2>
        {pendingItems.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="p-8 text-center text-gray-400">
              <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="font-medium">No pending approvals</p>
              <p className="text-sm mt-1">All {activeTab === 'so' ? 'sales' : 'purchase'} orders are up to date</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="pending-list">
            {pendingItems.map(order => renderOrderCard(order, true))}
          </div>
        )}
      </div>
      ) : (
      <div className="space-y-6">
        {/* SE Stock Pending */}
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-amber-500"></span>
            Pending Stock Approvals
            {data.pending_se_stock.length > 0 && <span className="text-sm font-normal text-gray-500">({data.pending_se_stock.length})</span>}
          </h2>
          {data.pending_se_stock.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="p-8 text-center text-gray-400">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="font-medium">No pending stock approvals</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="pending-se-stock-list">
              {data.pending_se_stock.map(req => (
                <SEStockCard
                  key={req.id} req={req} processing={processing}
                  onApprove={handleApproveSEStock}
                  onReject={(r) => setRejectModal({ id: r.id, type: 'se-stock', number: `SE Stock Request` })}
                />
              ))}
            </div>
          )}
        </div>

        {/* SE Stock Rejected */}
        {data.rejected_se_stock.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-500"></span>
              Rejected
              <span className="text-sm font-normal text-gray-500">({data.rejected_se_stock.length})</span>
            </h2>
            <div className="space-y-3" data-testid="rejected-se-stock-list">
              {data.rejected_se_stock.map(req => (
                <Card key={req.id} className="border-l-4 opacity-70" style={{ borderLeftColor: '#ef4444' }}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded-full border border-red-300">REJECTED</span>
                      <span className="text-xs text-gray-500">{req.rejected_by_name} &middot; {new Date(req.rejected_at).toLocaleDateString('en-IN')}</span>
                    </div>
                    <p className="text-sm text-gray-700">Requested by: {req.requested_by_name}</p>
                    {req.rejection_reason && <p className="text-sm text-red-600 mt-1">Reason: {req.rejection_reason}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
      )}

      {/* Rejected Section (SO/PO) */}
      {rejectedItems.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-500"></span>
            Rejected
            <span className="text-sm font-normal text-gray-500">({rejectedItems.length})</span>
          </h2>
          <div className="space-y-3" data-testid="rejected-list">
            {rejectedItems.map(order => renderOrderCard(order, false))}
          </div>
        </div>
      )}

      <RejectModal
        rejectModal={rejectModal} setRejectModal={setRejectModal}
        rejectReason={rejectReason} setRejectReason={setRejectReason}
        handleReject={handleReject} processing={processing}
      />

      <DesignImageModal designImage={designImage} setDesignImage={setDesignImage} />
    </div>
  );
}
